import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { defaultStorage } from 'aws-amplify/utils';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  IS_BYPASS_MODE,
  SSO_CONFIG_OUTPUTS,
  SSO_CUSTOM_PROVIDER,
} from '#shared';

import { getAuthToken } from '#shared/api';

cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage);
Amplify.configure(SSO_CONFIG_OUTPUTS);

type AuthProviderProps = {
  isLoggedIn: boolean;
  // checkPermission: (permission: PermissionType) => boolean;
  signIn: () => Promise<void>;
  getUserSession: () => Promise<void>;
  logout: () => Promise<void>;
};

export const AuthContext = createContext<AuthProviderProps | undefined>(
  undefined,
);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const signIn = useCallback(async () => {
    await signInWithRedirect({ provider: { custom: SSO_CUSTOM_PROVIDER } });
  }, []);

  const getUserSession = useCallback(async () => {
    // Skip authentication if bypass mode is enabled
    if (IS_BYPASS_MODE) {
      setIsLoggedIn(true);
      return;
    }

    const authToken = await getAuthToken();
    setIsLoggedIn(!!authToken);

    if (authToken) {
      if (location.pathname.includes('/sign-in')) {
        navigate('/');
      }
    } else {
      await signIn();
    }
  }, [location.pathname, navigate, signIn]);

  useEffect(() => {
    // Initial auth check - this is intentional and not a cascading render
    // eslint-disable-next-line react-hooks/set-state-in-effect
    getUserSession();
  }, [getUserSession]);

  const logout = useCallback(async () => {
    await signOut();
  }, []);

  const contextValue = useMemo(
    () => ({
      isLoggedIn,
      signIn,
      getUserSession,
      // checkPermission,
      logout,
    }),
    [isLoggedIn, getUserSession, signIn, logout],
  );

  return <AuthContext value={contextValue}>{children}</AuthContext>;
};
